import { useEffect } from "react";
import { channelList } from "../constants/channel-list";
import useAudioPlayerStore from "../store/audioPlayerStore";

export function useRadioPlayer(audioMainRef, audioNextRef) {
  const {
    isPlaying,
    setIsPlaying,
    isBuffering,
    setIsBuffering,
    currentChannel,
    setCurrentChannel,
    tracks,
    setTracks,
    currentTrack,
    setCurrentTrack,
  } = useAudioPlayerStore();

  const getCurrentTrackIndex = (tracks) => {
    const totalDuration = tracks.reduce((sum, track) => sum + track.duration, 0);
    const startTime = new Date().setHours(0, 0, 0, 0);
    const now = Date.now();
    let elapsedTime = Math.floor((now - startTime) / 1000) % totalDuration;

    let trackStart = 0;
    for (let i = 0; i < tracks.length; i++) {
      if (elapsedTime < trackStart + tracks[i].duration) {
        return { index: i, offset: elapsedTime - trackStart };
      }
      trackStart += tracks[i].duration;
    }
    return { index: 0, offset: 0 };
  };

  const preloadNextTrack = (index) => {
    const nextIndex = (index + 1) % tracks.length;
    const audioNext = audioNextRef?.current;

    if (audioNext) {
      audioNext.src = tracks[nextIndex].src;
      audioNext.load();
    }
  };

  const playNextTrack = (currentIndex) => {
    const nextIndex = (currentIndex + 1) % tracks.length;
    setCurrentTrack(tracks[nextIndex]);

    const temp = audioMainRef?.current;
    audioMainRef.current = audioNextRef?.current;
    audioNextRef.current = temp;

    audioMainRef?.current?.play().catch(console.error);
    preloadNextTrack(nextIndex);
  };

  const togglePlayPause = () => {
    if (!audioMainRef?.current) return;
    audioMainRef.current.muted = isPlaying;
    setIsPlaying(!isPlaying);
  };

  const switchChannelByIndex = (index) => {
    const newChannel = channelList[index];
    setCurrentChannel(newChannel);
    setTracks(newChannel.channelTracks);
    const { index: trackIndex, offset } = getCurrentTrackIndex(newChannel.channelTracks);
    setCurrentTrack(newChannel.channelTracks[trackIndex]);

    const audioMain = audioMainRef?.current;
    if (audioMain) {
      setIsBuffering(true); 

      audioMain.src = newChannel.channelTracks[trackIndex].src;
      audioMain.currentTime = offset;

      const handleCanPlay = () => {
        setIsBuffering(false); 
        isPlaying && audioMain.play().catch(console.error);
        audioMain.removeEventListener("canplay", handleCanPlay);
      };

      audioMain.addEventListener("canplay", handleCanPlay);
    }

    preloadNextTrack(trackIndex);
  };

  const prevChannelHandler = () => {
    const currentChannelIndex = channelList.findIndex(
      (c) => c.channelTitle === currentChannel.channelTitle
    );

    const prevIndex = (currentChannelIndex - 1 + channelList.length) % channelList.length;
    switchChannelByIndex(prevIndex);
  };

  const nextChannelHandler = () => {
    const currentChannelIndex = channelList.findIndex(
      (c) => c.channelTitle === currentChannel.channelTitle
    );

    const nextIndex = (currentChannelIndex + 1) % channelList.length;
    switchChannelByIndex(nextIndex);
  };

  useEffect(() => {
    if (!tracks?.length) return;
    const { index, offset } = getCurrentTrackIndex(tracks);
    setCurrentTrack(tracks[index]);

    const audioMain = audioMainRef?.current;
    if (audioMain && tracks[index]) {
      audioMain.src = tracks[index].src;
      audioMain.currentTime = offset;
      isPlaying && audioMain.play().catch(console.error);
    }

    preloadNextTrack(index);
  }, [currentChannel]);

  useEffect(() => {
    const audioMain = audioMainRef?.current;
    const audioNext = audioNextRef?.current;

    if (!audioMain || !audioNext || !currentTrack) return;

    const handleTimeUpdate = () => {
      if (
        audioMain.currentTime >= currentTrack.duration - 2 &&
        audioNext.readyState >= 3
      ) {
        const currentIndex = tracks.findIndex((t) => t.src === currentTrack.src);
        playNextTrack(currentIndex);
      }
    };

    audioMain.addEventListener("timeupdate", handleTimeUpdate);
    return () => audioMain.removeEventListener("timeupdate", handleTimeUpdate);
  }, [currentTrack]);

  return {
    audioMainRef,
    audioNextRef,
    currentChannel,
    currentTrack,
    isPlaying,
    isBuffering,
    togglePlayPause,
    switchChannelByIndex,
    prevChannelHandler,
    nextChannelHandler,
  };
}
